/**
 * Perfume Garden - Lightweight JSON-based Quick View Modal
 * Uses Shopify's native /products/${handle}.js endpoint (2.7 KB JSON)
 * Eliminates browser memory exhaustion and "Aw, Snap!" crashes on mobile.
 */

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this._productCache = new Map();
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (!this.modalContent) this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (!this.modalContent) this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.openedBy = opener;

        // Set opener loading state
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner, .loading-spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Extract product handle
        const productUrl = opener.getAttribute('data-product-url') || opener.dataset.productUrl || '';
        let handle = opener.getAttribute('data-product-handle') || opener.dataset.productHandle;
        if (!handle && productUrl) {
          const match = productUrl.match(/\/products\/([a-zA-Z0-9\-_]+)/);
          if (match) handle = match[1];
        }

        if (!handle) {
          console.error('[QuickAdd] Could not determine product handle from opener:', opener);
          this._cleanOpener(opener);
          return;
        }

        // Show loading state in modal drawer immediately
        this.renderLoading();
        super.show(opener);

        // Fetch JSON product data (tiny ~2.7 KB JSON, 0 scripts, 0 apps)
        this.loadProduct(handle)
          .then((product) => {
            this.renderProduct(product);
          })
          .catch((err) => {
            console.error('[QuickAdd] Failed to load product JSON:', err);
            this.renderError('Unable to load product details. Please try again.');
          })
          .finally(() => {
            this._cleanOpener(opener);
          });
      }

      _cleanOpener(opener) {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        const spinner = opener.querySelector('.loading__spinner, .loading-spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      async loadProduct(handle) {
        if (this._productCache.has(handle)) {
          return this._productCache.get(handle);
        }
        const rootUrl = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${rootUrl}products/${handle}.js`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const product = await res.json();
        this._productCache.set(handle, product);
        return product;
      }

      renderLoading() {
        this.modalContent.innerHTML = `
          <div class="pq-loading">
            <div class="loading__spinner">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
            <span>Loading details...</span>
          </div>
        `;
      }

      renderError(msg) {
        this.modalContent.innerHTML = `
          <div class="pq-error-state">
            <p class="pq-error">${this._escape(msg)}</p>
            <button type="button" class="button button--secondary" onclick="this.closest('quick-add-modal').hide()">Close</button>
          </div>
        `;
      }

      renderProduct(product) {
        const variants = product.variants || [];
        let currentVariant = variants.find((v) => v.available) || variants[0] || {};
        const images = product.images || [];
        const featuredImg = currentVariant.featured_image?.src || product.featured_image || images[0] || '';
        const hasOptions = product.options && product.options.length > 0 && !(product.options.length === 1 && product.options[0].name === 'Title');

        const html = `
          <div class="pq-container" data-product-handle="${this._escape(product.handle)}">
            <!-- Gallery -->
            <div class="pq-gallery">
              <div class="pq-main-image-wrap">
                <img
                  id="pq-main-image"
                  src="${this._imgUrl(featuredImg, 600)}"
                  alt="${this._escape(product.title)}"
                  loading="eager"
                  width="400"
                  height="400"
                />
              </div>
              ${images.length > 1 ? `
                <div class="pq-thumbs">
                  ${images.map((img, idx) => `
                    <button type="button" class="pq-thumb-btn ${img === featuredImg ? 'is-active' : ''}" data-index="${idx}" data-img="${this._imgUrl(img, 600)}">
                      <img src="${this._imgUrl(img, 120)}" alt="Thumbnail ${idx + 1}" loading="lazy" width="56" height="56" />
                    </button>
                  `).join('')}
                </div>
              ` : ''}
            </div>

            <!-- Details -->
            <div class="pq-details">
              <div class="pq-vendor">${this._escape(product.vendor || 'PERFUME GARDEN')}</div>
              <h2 class="pq-title">
                <a href="${product.url || '/products/' + product.handle}">${this._escape(product.title)}</a>
              </h2>

              <div class="pq-price-wrap">
                <span class="pq-price" id="pq-price">${this.formatMoney(currentVariant.price)}</span>
                <span class="pq-compare-price ${currentVariant.compare_at_price > currentVariant.price ? '' : 'hidden'}" id="pq-compare-price">
                  ${currentVariant.compare_at_price ? this.formatMoney(currentVariant.compare_at_price) : ''}
                </span>
                <span class="pq-badge ${currentVariant.compare_at_price > currentVariant.price ? '' : 'hidden'}" id="pq-badge">
                  ${this.calcDiscount(currentVariant.price, currentVariant.compare_at_price)}
                </span>
              </div>

              <!-- Options / Variant Picker -->
              ${hasOptions ? `
                <div class="pq-options">
                  ${product.options.map((option, optIdx) => `
                    <div class="pq-option-group" data-option-index="${optIdx}">
                      <label class="pq-option-label">
                        ${this._escape(option.name)}: <span class="pq-option-selected-val">${this._escape(currentVariant.options ? currentVariant.options[optIdx] : option.values[0])}</span>
                      </label>
                      <div class="pq-pills">
                        ${option.values.map((val) => {
                          const isSelected = currentVariant.options && currentVariant.options[optIdx] === val;
                          return `
                            <button
                              type="button"
                              class="pq-pill ${isSelected ? 'is-selected' : ''}"
                              data-option-index="${optIdx}"
                              data-value="${this._escape(val)}"
                            >
                              ${this._escape(val)}
                            </button>
                          `;
                        }).join('')}
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}

              <!-- Add to Cart Form -->
              <form class="pq-form" id="pq-form" method="post" action="/cart/add">
                <input type="hidden" name="id" id="pq-variant-id" value="${currentVariant.id || ''}">
                
                <div class="pq-qty-wrapper">
                  <span class="pq-qty-label">Quantity</span>
                  <div class="pq-qty-selector">
                    <button type="button" class="pq-qty-btn pq-qty-minus" aria-label="Decrease quantity">−</button>
                    <input type="number" name="quantity" class="pq-qty-input" value="1" min="1" max="99" />
                    <button type="button" class="pq-qty-btn pq-qty-plus" aria-label="Increase quantity">+</button>
                  </div>
                </div>

                <div class="pq-form-error hidden" id="pq-form-error"></div>

                <button
                  type="submit"
                  class="pq-submit-btn ${currentVariant.available ? '' : 'disabled'}"
                  id="pq-submit-btn"
                  ${currentVariant.available ? '' : 'disabled'}
                >
                  <span class="pq-btn-text">${currentVariant.available ? 'ADD TO CART' : 'SOLD OUT'}</span>
                  <div class="loading__spinner hidden">
                    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </button>
              </form>

              <a href="${product.url || '/products/' + product.handle}" class="pq-view-details">
                View Full Details →
              </a>
            </div>
          </div>
        `;

        this.modalContent.innerHTML = html;
        this.initEventListeners(product);
      }

      initEventListeners(product) {
        const container = this.modalContent.querySelector('.pq-container');
        if (!container) return;

        let currentVariant = (product.variants || []).find((v) => v.available) || product.variants[0] || {};

        // 1. Gallery thumbnail switching
        const mainImg = container.querySelector('#pq-main-image');
        const thumbBtns = container.querySelectorAll('.pq-thumb-btn');
        thumbBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            thumbBtns.forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            if (mainImg) mainImg.src = btn.dataset.img;
          });
        });

        // 2. Option pill switching
        const optionGroups = container.querySelectorAll('.pq-option-group');
        const pills = container.querySelectorAll('.pq-pill');
        pills.forEach((pill) => {
          pill.addEventListener('click', () => {
            const group = pill.closest('.pq-option-group');
            if (!group) return;
            group.querySelectorAll('.pq-pill').forEach((p) => p.classList.remove('is-selected'));
            pill.classList.add('is-selected');

            const valLabel = group.querySelector('.pq-option-selected-val');
            if (valLabel) valLabel.textContent = pill.dataset.value;

            // Determine selected values across all option groups
            const selectedOptions = Array.from(optionGroups).map((g) => {
              const selPill = g.querySelector('.pq-pill.is-selected');
              return selPill ? selPill.dataset.value : null;
            });

            // Find matching variant
            const matched = product.variants.find((v) => {
              return v.options.every((opt, idx) => opt === selectedOptions[idx]);
            });

            if (matched) {
              currentVariant = matched;
              // Update variant ID
              const varInput = container.querySelector('#pq-variant-id');
              if (varInput) varInput.value = matched.id;

              // Update price
              const priceEl = container.querySelector('#pq-price');
              if (priceEl) priceEl.textContent = this.formatMoney(matched.price);

              // Update compare at price & badge
              const compEl = container.querySelector('#pq-compare-price');
              const badgeEl = container.querySelector('#pq-badge');
              if (matched.compare_at_price > matched.price) {
                if (compEl) {
                  compEl.textContent = this.formatMoney(matched.compare_at_price);
                  compEl.classList.remove('hidden');
                }
                if (badgeEl) {
                  badgeEl.textContent = this.calcDiscount(matched.price, matched.compare_at_price);
                  badgeEl.classList.remove('hidden');
                }
              } else {
                if (compEl) compEl.classList.add('hidden');
                if (badgeEl) badgeEl.classList.add('hidden');
              }

              // Update main image if variant has one
              if (matched.featured_image && matched.featured_image.src && mainImg) {
                mainImg.src = this._imgUrl(matched.featured_image.src, 600);
                thumbBtns.forEach((tb) => {
                  if (tb.dataset.img.includes(matched.featured_image.src.split('?')[0])) {
                    tb.classList.add('is-active');
                  } else {
                    tb.classList.remove('is-active');
                  }
                });
              }

              // Update button
              const submitBtn = container.querySelector('#pq-submit-btn');
              const btnText = submitBtn?.querySelector('.pq-btn-text');
              if (submitBtn) {
                if (matched.available) {
                  submitBtn.removeAttribute('disabled');
                  submitBtn.classList.remove('disabled');
                  if (btnText) btnText.textContent = 'ADD TO CART';
                } else {
                  submitBtn.setAttribute('disabled', 'true');
                  submitBtn.classList.add('disabled');
                  if (btnText) btnText.textContent = 'SOLD OUT';
                }
              }
            }
          });
        });

        // 3. Quantity Stepper
        const qtyInput = container.querySelector('.pq-qty-input');
        const minusBtn = container.querySelector('.pq-qty-minus');
        const plusBtn = container.querySelector('.pq-qty-plus');

        if (minusBtn && qtyInput) {
          minusBtn.addEventListener('click', () => {
            let val = parseInt(qtyInput.value) || 1;
            if (val > 1) qtyInput.value = val - 1;
          });
        }
        if (plusBtn && qtyInput) {
          plusBtn.addEventListener('click', () => {
            let val = parseInt(qtyInput.value) || 1;
            qtyInput.value = val + 1;
          });
        }
        if (qtyInput) {
          qtyInput.addEventListener('change', () => {
            let val = parseInt(qtyInput.value) || 1;
            if (val < 1) val = 1;
            qtyInput.value = val;
          });
        }

        // 4. Form Submit (Add to Cart)
        const form = container.querySelector('#pq-form');
        const submitBtn = container.querySelector('#pq-submit-btn');
        const errorEl = container.querySelector('#pq-form-error');

        if (form && submitBtn) {
          form.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (submitBtn.classList.contains('loading') || submitBtn.hasAttribute('disabled')) return;

            const variantId = container.querySelector('#pq-variant-id')?.value;
            const quantity = parseInt(qtyInput?.value) || 1;
            if (!variantId) return;

            // Loading state
            submitBtn.classList.add('loading');
            const spinner = submitBtn.querySelector('.loading__spinner');
            if (spinner) spinner.classList.remove('hidden');
            if (errorEl) errorEl.classList.add('hidden');

            try {
              const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
              const bodyData = {
                items: [{ id: parseInt(variantId), quantity: quantity }],
              };
              if (cart && typeof cart.getSectionsToRender === 'function') {
                bodyData.sections = cart.getSectionsToRender().map((s) => s.id);
                bodyData.sections_url = window.location.pathname;
              }

              const rootUrl = window.Shopify?.routes?.root || '/';
              const cartAddUrl = window.routes?.cart_add_url || `${rootUrl}cart/add`;
              const response = await fetch(`${cartAddUrl}.js`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                body: JSON.stringify(bodyData),
              });

              const data = await response.json();

              if (data.status) {
                // Shopify returned an error
                const msg = data.description || data.message || 'Could not add to cart';
                if (errorEl) {
                  errorEl.textContent = msg;
                  errorEl.classList.remove('hidden');
                }
                return;
              }

              // Success! Close Quick View
              this.hide(true);

              // Update & Open Cart Drawer FIRST
              if (cart) {
                if (data.sections) {
                  cart.renderContents(data);
                } else if (typeof cart.getSectionsToRender === 'function') {
                  const sections = cart.getSectionsToRender().map((s) => s.id);
                  const sResp = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${sections.join(',')}`);
                  const sData = await sResp.json();
                  cart.renderContents({ sections: sData });
                }
              }

              // Update cart count badge
              fetch((window.routes?.cart_url || '/cart') + '.js')
                .then((r) => r.json())
                .then((cartData) => {
                  document.querySelectorAll('.cart-count-bubble, .cart-count').forEach((el) => {
                    const span = el.querySelector('span[aria-hidden="true"]') || el.querySelector('span') || el;
                    if (span) span.textContent = cartData.item_count;
                    if (cartData.item_count > 0) {
                      el.classList.remove('hidden');
                      el.style.display = '';
                    }
                  });
                })
                .catch(() => {});

              // Publish cart update event
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: data });
              }

              // Toast
              if (window.PGToast && typeof window.PGToast.show === 'function') {
                window.PGToast.show('Added to Cart! 🛒');
              }
            } catch (err) {
              console.error('[QuickAdd] Add to Cart failed:', err);
              if (errorEl) {
                errorEl.textContent = 'Something went wrong. Please try again.';
                errorEl.classList.remove('hidden');
              }
            } finally {
              submitBtn.classList.remove('loading');
              if (spinner) spinner.classList.add('hidden');
            }
          });
        }
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') cents = 0;
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || 'Rs. {{amount}}');
        }
        const currency = window.Shopify?.currency?.active || 'INR';
        try {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (e) {
          return '₹' + Math.round(cents / 100);
        }
      }

      calcDiscount(price, compare) {
        if (!compare || compare <= price) return '';
        const pct = Math.round(((compare - price) / compare) * 100);
        return `${pct}% OFF`;
      }

      _imgUrl(src, width) {
        if (!src) return '';
        const clean = src.replace(/^\/\//, 'https://');
        const sep = clean.includes('?') ? '&' : '?';
        return `${clean}${sep}width=${width}`;
      }

      _escape(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
